import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { GitHubSourceSettingsApi } from "./GitHubSourceSettings.Api";
import type { GitHubSourceSettingsViewModel } from "./GitHubSourceSettings.ViewModel.types";
import type {
  GitHubConfiguration,
  GitHubRepository,
} from "./GitHubSourceSettings.types";

const normalizeId = (value: unknown): string => String(value ?? "");

export const useGitHubSourceSettingsViewModel = (
  model: SourceSettingsModel,
): GitHubSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [repositories, setRepositories] = useState<GitHubRepository[]>([]);
  const [repositoriesLoaded, setRepositoriesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.GitHub && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<GitHubConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<GitHubConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        repositories: cfg.repositories ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity =
      `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setRepositories([]);

    updateConnectionConfiguration<GitHubConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        repositories: [],
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadRepositories = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setRepositories([]);
        setRepositoriesLoaded(true);
        return;
      }

      const githubApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<GitHubSourceSettingsApi>(
          SourceTypeIds.GitHub,
        );
      if (!githubApi) {
        setRepositories([]);
        setRepositoriesLoaded(true);
        return;
      }

      setRepositoriesLoaded(false);
      setHasError(false);
      try {
        setRepositories(
          await githubApi.getRepositories(model.boardId, editingConnectionId),
        );
      } catch {
        setRepositories([]);
        setHasError(true);
        onUpdateRef.current({ isAuthorised: false });
      } finally {
        setRepositoriesLoaded(true);
      }
    };

    void loadRepositories();
  }, [editingConnectionId, isVisible, model.boardId]);

  return {
    isVisible,
    repositories,
    repositoryOptions: repositories.map((repository) => ({
      id: normalizeId(repository.id),
      label: repository.name || normalizeId(repository.id),
    })),
    repositoriesLoaded,
    hasError,
    selectedRepositoryIds: (configuration.repositories ?? []).map((repository) =>
      normalizeId(repository.id),
    ),
    assignedToMeFixed: true,
    onSelectRepositories: (repositoryIds: string[]) => {
      const selected = repositories.filter((repository) =>
        repositoryIds.includes(normalizeId(repository.id)),
      );

      updateConnectionConfiguration<GitHubConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          repositories: selected,
        }),
      );
    },
  };
};
